import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import '../styles/AnalyticsDashboard.css';

interface AnalyticsDashboardProps {
  token: string;
  apiBase: string;
}

interface VisitorAnalytics {
  totalPageViews: number;
  uniqueVisitors: number;
  newVisitors: number;
  returningVisitors: number;
  todayViews: number;
  dailyVisits: { date: string; count: number }[];
  hourlyDistribution: { hour: number; count: number }[];
  recentVisitors: {
    ip_hash: string;
    user_agent: string;
    referrer: string;
    page_path: string;
    timestamp: string;
    isReturnVisitor: boolean;
  }[];
}

interface SystemMetrics {
  cpu: { usage: number; cores: number };
  memory: { total: number; used: number; free: number; usagePercent: number };
  disk: { total: number; used: number; free: number; usagePercent: number };
  network: { received: number; transmitted: number };
  uptime: number;
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ token, apiBase }) => {
  const [analytics, setAnalytics] = useState<VisitorAnalytics | null>(null);
  const [system, setSystem] = useState<SystemMetrics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [systemLoading, setSystemLoading] = useState(true);
  const [dateRange, setDateRange] = useState<7 | 30>(7);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await fetch(`${apiBase}/analytics/visitors?days=${dateRange}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [apiBase, token, dateRange]);

  const fetchSystem = useCallback(async () => {
    try {
      const response = await fetch(`${apiBase}/analytics/system`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSystem(data);
      }
    } catch (err) {
      console.error('Failed to fetch system metrics:', err);
    } finally {
      setSystemLoading(false);
    }
  }, [apiBase, token]);

  useEffect(() => {
    fetchAnalytics();
    fetchSystem();

    const systemInterval = setInterval(fetchSystem, 5000);
    const analyticsInterval = setInterval(fetchAnalytics, 30000);

    return () => {
      clearInterval(systemInterval);
      clearInterval(analyticsInterval);
    };
  }, [fetchAnalytics, fetchSystem]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getBrowserFromUA = (ua: string) => {
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Other';
  };

  const getNewVsReturningPercent = () => {
    if (!analytics || analytics.totalPageViews === 0) return { new: 0, returning: 0 };
    const total = analytics.newVisitors + analytics.returningVisitors;
    if (total === 0) return { new: 0, returning: 0 };
    return {
      new: Math.round((analytics.newVisitors / total) * 100),
      returning: Math.round((analytics.returningVisitors / total) * 100)
    };
  };

  const percentages = getNewVsReturningPercent();

  const hourlyData = Array.from({ length: 24 }, (_, i) => {
    const existing = analytics?.hourlyDistribution.find(h => h.hour === i);
    return { hour: `${i}:00`, count: existing?.count || 0 };
  });

  return (
    <div className="analytics-dashboard">
      <div className="analytics-header">
        <h2>Analytics Dashboard</h2>
        <div className="date-range-selector">
          <button
            className={dateRange === 7 ? 'active' : ''}
            onClick={() => setDateRange(7)}
          >
            7 Days
          </button>
          <button
            className={dateRange === 30 ? 'active' : ''}
            onClick={() => setDateRange(30)}
          >
            30 Days
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon visitors">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">
              {analyticsLoading ? '...' : analytics?.uniqueVisitors || 0}
            </span>
            <span className="stat-label">Unique Visitors</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon views">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">
              {analyticsLoading ? '...' : analytics?.totalPageViews || 0}
            </span>
            <span className="stat-label">Page Views</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon today">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">
              {analyticsLoading ? '...' : analytics?.todayViews || 0}
            </span>
            <span className="stat-label">Today's Views</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon ratio">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" />
              <line x1="22" y1="11" x2="16" y2="11" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{percentages.new}%</span>
            <span className="stat-label">New Visitors</span>
          </div>
        </div>
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <h3>Visitor Timeline</h3>
          {analyticsLoading ? (
            <div className="chart-loading">
              <div className="loading-spinner" />
            </div>
          ) : analytics?.dailyVisits && analytics.dailyVisits.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={analytics.dailyVisits.map(d => ({ ...d, date: formatDate(d.date) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a2e',
                    border: '1px solid #334155',
                    borderRadius: '8px'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#40916C"
                  strokeWidth={2}
                  dot={{ fill: '#40916C', strokeWidth: 2 }}
                  name="Visits"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data">No visitor data yet</div>
          )}
        </div>

        <div className="chart-card">
          <h3>Hourly Distribution</h3>
          {analyticsLoading ? (
            <div className="chart-loading">
              <div className="loading-spinner" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="hour" stroke="#94a3b8" fontSize={10} interval={3} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a2e',
                    border: '1px solid #334155',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="count" fill="#2D6A4F" name="Visits" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="section-header">
        <h3>System Metrics</h3>
        <span className="live-indicator">
          <span className="pulse" />
          Live
        </span>
      </div>

      <div className="system-grid">
        <div className="system-card">
          <div className="system-header">
            <span className="system-label">CPU Usage</span>
            <span className="system-value">{systemLoading ? '...' : `${system?.cpu.usage || 0}%`}</span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill cpu"
              style={{ width: `${system?.cpu.usage || 0}%` }}
            />
          </div>
          <span className="system-detail">{system?.cpu.cores || 0} cores</span>
        </div>

        <div className="system-card">
          <div className="system-header">
            <span className="system-label">Memory</span>
            <span className="system-value">
              {systemLoading ? '...' : `${system?.memory.usagePercent || 0}%`}
            </span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill memory"
              style={{ width: `${system?.memory.usagePercent || 0}%` }}
            />
          </div>
          <span className="system-detail">
            {system ? `${system.memory.used} MB / ${system.memory.total} MB` : '...'}
          </span>
        </div>

        <div className="system-card">
          <div className="system-header">
            <span className="system-label">Disk</span>
            <span className="system-value">
              {systemLoading ? '...' : `${system?.disk.usagePercent || 0}%`}
            </span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill disk"
              style={{ width: `${system?.disk.usagePercent || 0}%` }}
            />
          </div>
          <span className="system-detail">
            {system ? `${system.disk.used} GB / ${system.disk.total} GB` : '...'}
          </span>
        </div>

        <div className="system-card">
          <div className="system-header">
            <span className="system-label">Network I/O</span>
            <span className="system-value uptime">
              {systemLoading ? '...' : formatUptime(system?.uptime || 0)}
            </span>
          </div>
          <div className="network-stats">
            <div className="network-stat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
              <span>{system?.network.transmitted || 0} MB</span>
            </div>
            <div className="network-stat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12l7 7 7-7" />
              </svg>
              <span>{system?.network.received || 0} MB</span>
            </div>
          </div>
        </div>
      </div>

      <div className="section-header">
        <h3>Recent Visitors</h3>
      </div>

      <div className="visitors-table-container">
        {analyticsLoading ? (
          <div className="table-loading">
            <div className="loading-spinner" />
          </div>
        ) : analytics?.recentVisitors && analytics.recentVisitors.length > 0 ? (
          <table className="visitors-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Browser</th>
                <th>Page</th>
                <th>Referrer</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {analytics.recentVisitors.map((visitor, index) => (
                <tr key={index}>
                  <td>{formatTimestamp(visitor.timestamp)}</td>
                  <td>{getBrowserFromUA(visitor.user_agent)}</td>
                  <td>{visitor.page_path || '/'}</td>
                  <td className="referrer-cell">
                    {visitor.referrer ? (() => {
                      try {
                        return new URL(visitor.referrer).hostname;
                      } catch {
                        return 'Direct';
                      }
                    })() : 'Direct'}
                  </td>
                  <td>
                    <span className={`visitor-badge ${visitor.isReturnVisitor ? 'returning' : 'new'}`}>
                      {visitor.isReturnVisitor ? 'Returning' : 'New'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="no-data">No visitors recorded yet</div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
